import {
  collection,
  doc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { firebaseHelper } from "ui";
import type {
  StudioLibraryItemInterface,
  StudioLibrarySource,
} from "ts-interfaces";

/**
 * Studio Library: one doc per (workspace, source, product) with a
 * deterministic id, so every write is a read-free `setDoc(merge)` +
 * `increment()`. `uid` is the LAST writer; team docs are gated by firm
 * membership. Every write carries the display fields it's given; the store
 * passes the stored row's values when it knows them.
 */

const STUDIO_LIBRARY_COLLECTION = "studioLibrary";
/** The most rows one workspace read returns; a full page may not be all of them. */
export const STUDIO_LIBRARY_PAGE_LIMIT = 500;
const BATCH_LIMIT = 400;
// Firestore's cap on an `in` filter.
const IN_LIMIT = 30;

export interface StudioLibraryRef {
  productId: string;
  source: StudioLibrarySource;
  name: string;
  supplier?: string;
  thumbnail?: string;
  productType?: string;
}

/** Workspace scope half of every Studio Library id. */
const studioLibraryScope = (uid: string, firmId: string | null): string =>
  firmId ? `f_${firmId}` : `u_${uid}`;

export const studioLibraryDocId = (
  uid: string,
  firmId: string | null,
  source: StudioLibrarySource,
  productId: string,
): string => `${studioLibraryScope(uid, firmId)}__${source}__${productId}`;

const stripEmpty = <T extends Record<string, any>>(o: T): T =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined && v !== ""),
  ) as T;

/** Identity plus the ref's display fields (empty ones are left out). */
export const studioLibraryFields = (
  uid: string,
  firmId: string | null,
  ref: StudioLibraryRef,
) =>
  stripEmpty({
    id: studioLibraryDocId(uid, firmId, ref.source, ref.productId),
    uid,
    design_firm_id: firmId,
    productId: ref.productId,
    source: ref.source,
    name: ref.name,
    supplier: ref.supplier,
    thumbnail: ref.thumbnail,
    productType: ref.productType,
  });

const itemDoc = (id: string) => doc(firebaseHelper.db, STUDIO_LIBRARY_COLLECTION, id);

/** A board placement / material apply: upsert + bump the count. */
export async function recordStudioLibraryUse(
  uid: string,
  firmId: string | null,
  ref: StudioLibraryRef,
): Promise<void> {
  const data = studioLibraryFields(uid, firmId, ref);
  await setDoc(
    itemDoc(data.id),
    { ...data, useCount: increment(1), lastUsedAt: Date.now() },
    { merge: true },
  );
}

/**
 * Manual "Add to Studio Library" for refs the caller knows are new: created at
 * count 0. `onCommit` gets each committed batch's refs, so a caller can undo
 * only what never landed when a later batch fails.
 */
export async function addToStudioLibrary(
  uid: string,
  firmId: string | null,
  refs: StudioLibraryRef[],
  onCommit?: (committed: StudioLibraryRef[]) => void,
): Promise<void> {
  const now = Date.now();
  for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
    const chunk = refs.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(firebaseHelper.db);
    for (const ref of chunk) {
      const data = studioLibraryFields(uid, firmId, ref);
      batch.set(
        itemDoc(data.id),
        { ...data, useCount: increment(0), lastUsedAt: now },
        { merge: true },
      );
    }
    await batch.commit();
    onCommit?.(chunk);
  }
}

/** `onCommit` gets each committed batch's ids, as for addToStudioLibrary. */
export async function removeFromStudioLibrary(
  ids: string[],
  onCommit?: (committed: string[]) => void,
): Promise<void> {
  for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
    const chunk = ids.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(firebaseHelper.db);
    for (const id of chunk) batch.delete(itemDoc(id));
    await batch.commit();
    onCommit?.(chunk);
  }
}

export const sortStudioItems = <T extends { useCount: number; lastUsedAt: number }>(
  items: T[],
): T[] =>
  [...items].sort(
    (a, b) => b.useCount - a.useCount || b.lastUsedAt - a.lastUsedAt,
  );

// Personal reads the caller's own unstamped docs; a team reads the firm's.
// `uid` alone would return every team doc the user last touched. The rules
// can only allow a list query that carries these constraints.
const scopeOf = (uid: string, firmId: string | null) =>
  firmId
    ? [where("design_firm_id", "==", firmId)]
    : [where("uid", "==", uid), where("design_firm_id", "==", null)];

const studioCollection = () =>
  collection(firebaseHelper.db, STUDIO_LIBRARY_COLLECTION);

/** Items for one workspace, most used first (the top STUDIO_LIBRARY_PAGE_LIMIT). */
export async function getStudioLibraryItems(
  uid: string,
  activeFirmId: string | null,
): Promise<StudioLibraryItemInterface[]> {
  if (!uid) return [];
  const snap = await getDocs(
    query(
      studioCollection(),
      ...scopeOf(uid, activeFirmId),
      orderBy("useCount", "desc"),
      orderBy("lastUsedAt", "desc"),
      limit(STUDIO_LIBRARY_PAGE_LIMIT),
    ),
  );
  return snap.docs.map((d) => d.data() as StudioLibraryItemInterface);
}

/** The workspace's docs among `ids`, whatever their rank; missing ids are skipped. */
export async function getStudioLibraryDocs(
  uid: string,
  firmId: string | null,
  ids: string[],
): Promise<StudioLibraryItemInterface[]> {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += IN_LIMIT) chunks.push(ids.slice(i, i + IN_LIMIT));
  const snaps = await Promise.all(
    chunks.map((chunk) =>
      getDocs(query(studioCollection(), ...scopeOf(uid, firmId), where("id", "in", chunk))),
    ),
  );
  return snaps.flatMap((snap) =>
    snap.docs.map((d) => d.data() as StudioLibraryItemInterface),
  );
}
